import * as React from 'react';
import withStyles from 'react-jss';
import { CssType, ThemeType } from '@theming/jssTypes';
import { formatDateTime } from '@utils/utils';
import { getOrderStatusPrettyName } from '@components/pages/FoodDashboard/utils';
import { useTranslation } from 'react-i18next';
import { ReadWithSignedUrl } from '@components/utilities/ReadWithSignedUrl';
import Modal from 'src/design-system/components/atoms/Modal/Modal';
import FixedHeaderTable from 'src/design-system/components/molecules/FixedHeaderTable/FixedHeaderTable';
import Image from 'src/design-system/components/atoms/Image/Image';

const styles = (theme: ThemeType): CssType => ({
  tableContainer: {
    flex: '1 1 0',
    overflow: 'auto',
    height: '300px',
    boxShadow: theme.shadow.sm,
    '& .ant-table-tbody > tr > td, .ant-table-thead > tr > th': {
      padding: theme.spacing[2],
    },
  },
  image: {
    maxHeight: '450px',
    maxWidth: '450px',
  },
  descriptionContainer: {
    marginBottom: theme.spacing[2],
  },
});

const TableModal = (props) => {
  const { t } = useTranslation();
  const {
    columnsToShow,
    dataSource,
    isVisible,
    customGenerateDataColumns,
    useCustomGenerateDataColumns = false,
    classes,
    header,
    handleModalClose,
    showDescription = false,
    descriptionText,
    width = '900px',
  } = props;
  const generateDataColumns = () => {
    const columnData: any = [
      {
        title: t('S.No.'),
        key: 'index',
        render: (text, record, index) => index + 1,
        width: 20,
      },
    ];
    const indexList = columnsToShow;
    indexList.forEach((item) => {
      const itemToPush = {};
      itemToPush['title'] = t(item.pretty_name);
      itemToPush['dataIndex'] = item.column_id;
      itemToPush['width'] = item?.width || 50;
      itemToPush['key'] = item.column_id;

      if (item.column_id === 'delivery_image' || item.column_id === 'image') {
        itemToPush['render'] = (x, record) => {
          return x ? (
            <Image.PreviewGroup>
              {item.is_signed_url ? (
                <ReadWithSignedUrl
                  url={x}
                  renderingCb={(signedUrl) => (
                    <Image className={classes.image} src={signedUrl} placeholder={true} />
                  )}
                />
              ) : (
                <Image className={classes.image} src={x} placeholder={true} />
              )}
            </Image.PreviewGroup>
          ) : t('Not Available');
        };
      }

      if (item.column_id === 'assigned_time' || item.column_id === 'timestamp') {
        itemToPush['render'] = (x, record) => {
          return <span>{formatDateTime(x)}</span>;
        };
      }

      if (item.pretty_name === 'Order Status') {
        itemToPush['render'] = (x, record) => {
          return <span>{getOrderStatusPrettyName(x)}</span>;
        };
      }
      columnData.push(itemToPush);
    });
    return columnData;
  };
  return (
    <Modal
      title={header}
      width={width}
      visible={isVisible}
      footer={null}
      onCancel={() => handleModalClose(false)}
      destroyOnClose
    >
      {
        showDescription && descriptionText ?
          <div className={classes.descriptionContainer}>
            {<span>{descriptionText}</span>}
          </div> : null
      }
      <div className={classes.tableContainer}>
        <FixedHeaderTable
          locale={{ emptyText: `No ${header}` }}
          pagination={false}
          dataSource={dataSource}
          columns={useCustomGenerateDataColumns ?
            customGenerateDataColumns(columnsToShow) : generateDataColumns()}
        />
      </div>
    </Modal>
  );
};

const StyledTableModal = withStyles(styles, { injectTheme: true })(TableModal);
export default StyledTableModal;
